import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";

import ContentfulUtil from "../util/ContentfulUtil";
import PageTitle from "../components/PageTitle";
import PageContent from "../components/PageContent";
import BlogGallery from "../components/blog/BlogGallery";
import ImageLightbox from "../components/blog/ImageLightbox";
import NotFound from "./NotFound";

export default function SingleBlog() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const blogPost = await ContentfulUtil.getBlogPostBySlug(slug);
        if (!blogPost) {
          setError(true);
        } else {
          setPost(blogPost);
        }
      } catch (err) {
        console.error("Error loading blog post:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <>
        <PageTitle>Loading Blog Post...</PageTitle>
        <PageContent>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </PageContent>
      </>
    );
  }

  if (error || !post) {
    return <NotFound />;
  }

  const richTextOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <p className="paragraph mb-4">{children}</p>
      ),
      [BLOCKS.HEADING_1]: (node, children) => (
        <h1 className="mb-4 mt-5 text-secondary">{children}</h1>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <h2 className="mb-3 mt-4 text-secondary">{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <h3 className="mb-3 mt-4 text-secondary">{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <h4 className="mb-2 mt-3 text-secondary">{children}</h4>
      ),
      [BLOCKS.QUOTE]: (node, children) => (
        <blockquote
          className="blockquote p-4 my-4 bg-light border-left border-primary"
          style={{ borderLeftWidth: "4px" }}
        >
          <p className="mb-0 font-italic text-dark">{children}</p>
        </blockquote>
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <ul className="list-styled mb-4">{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (node, children) => (
        <ol className="mb-4 pl-4">{children}</ol>
      ),
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const asset = node.data?.target?.fields;
        if (!asset?.file?.url) return null;
        const assetUrl = asset.file.url.startsWith("//")
          ? `https:${asset.file.url}`
          : asset.file.url;
        return (
          <div className="my-4 text-center">
            <img
              src={assetUrl}
              alt={asset.description || asset.title || "Blog illustration"}
              className="img-fluid rounded"
            />
          </div>
        );
      },
      [INLINES.HYPERLINK]: (node, children) => (
        <a
          href={node.data.uri}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary font-weight-bold"
        >
          {children}
        </a>
      ),
    },
  };

  const renderContent = (content) => {
    if (!content) return null;
    if (typeof content === "string") {
      return <p className="paragraph mb-4">{content}</p>;
    }
    if (content.nodeType === "document") {
      return documentToReactComponents(content, richTextOptions);
    }
    return null;
  };

  return (
    <>
      <PageTitle>
        <div className="row">
          <div className="col-12">
            {post.category && (
              <span
                className="badge badge-primary text-white mb-2 text-uppercase"
                style={{ fontSize: "12px", letterSpacing: "1px" }}
              >
                {post.category}
              </span>
            )}
            <h1
              className="text-white font-secondary mb-3"
              style={{
                fontSize: "clamp(24px, 5vw, 42px)",
                lineHeight: "1.25",
                wordBreak: "break-word",
              }}
            >
              {post.title}
            </h1>

            <div
              className="d-flex flex-wrap align-items-center text-white"
              style={{
                fontFamily: '"Poppins", sans-serif',
                fontSize: "13px",
                fontWeight: "400",
                gap: "8px 18px",
                opacity: 0.9,
                lineHeight: "1.4",
              }}
            >
              <span className="d-inline-flex align-items-center">
                <i className="ti-calendar text-primary mr-1" />
                {post.getFormattedDate()}
              </span>
              {post.author && (
                <span className="d-inline-flex align-items-center">
                  <i className="ti-user text-primary mr-1" />
                  By {post.author}
                </span>
              )}
            </div>
          </div>
        </div>
      </PageTitle>

      <PageContent>
        <div className="row">
          <div className="col-lg-10 mx-auto">
            {/* Primary Cover / Featured Image */}
            {post.featuredImage && (
              <div className="mb-4 text-center">
                <div
                  className="position-relative d-inline-block"
                  style={{ cursor: "pointer", maxWidth: "100%" }}
                  onClick={() => openLightbox(0)}
                  title="Click to enlarge & browse photos"
                >
                  <img
                    src={post.featuredImage.url}
                    alt={post.featuredImage.description || post.title}
                    className="img-fluid rounded"
                    style={{
                      maxHeight: "420px",
                      maxWidth: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                      margin: "0 auto",
                      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.01)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: "12px",
                      right: "12px",
                      backgroundColor: "rgba(26, 26, 55, 0.85)",
                      color: "#ffffff",
                      padding: "6px 14px",
                      borderRadius: "20px",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                      pointerEvents: "none",
                    }}
                  >
                    <i className="ti-zoom-in" />
                    <span>
                      {post.images.length > 1
                        ? `Browse All Photos (${post.images.length})`
                        : "Click to Enlarge"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Main Blog Content */}
            <div className="blog-content mb-5">
              {renderContent(post.content)}
            </div>

            {/* Photo Gallery for additional images (Option A) */}
            <BlogGallery
              images={post.galleryImages}
              onImageClick={(idx) =>
                openLightbox(post.featuredImage ? idx + 1 : idx)
              }
            />

            {/* Back to Blog Navigation */}
            <div
              className="mt-5 pt-4 border-top d-flex flex-wrap justify-content-between align-items-center"
              style={{ gap: "12px" }}
            >
              <Link
                to="/blog"
                className="btn btn-primary-outline"
                style={{
                  padding: "10px 20px",
                  fontSize: "14px",
                  whiteSpace: "normal",
                  textAlign: "center",
                  maxWidth: "100%",
                }}
              >
                <i className="ti-arrow-left mr-2" /> Back to All Posts
              </Link>
              <a
                href="#/"
                className="btn btn-light"
                style={{
                  padding: "10px 20px",
                  fontSize: "14px",
                }}
              >
                Home
              </a>
            </div>
          </div>
        </div>

        {/* Lightbox for navigating through all photos */}
        <ImageLightbox
          images={post.images}
          currentIndex={lightboxIndex}
          isOpen={lightboxOpen}
          onClose={closeLightbox}
          onNavigate={setLightboxIndex}
        />
      </PageContent>
    </>
  );
}
